import "./routesummary.css";
import cx from "classnames";
import {
  BsInfoCircle,
  BsSpeedometer2,
  BsClockHistory,
  BsSignpostSplit,
  BsArrowRight,
  BsGeoAltFill,
  BsLayers,
} from "react-icons/bs";

//============== HELPERS ===============
const formatDistance = (meters = 0) => {
  if (meters < 1000) {
    return `${meters} m`;
  }

  return `${(meters / 1000).toFixed(2)} km`;
};

const formatDuration = (seconds = 0) => {
  if (seconds < 60) {
    return `${seconds} sec`;
  }

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  if (!remainingSeconds) {
    return `${minutes} min`;
  }

  return `${minutes} min ${remainingSeconds} sec`;
};

const RouteSummary = ({
  routeDraft,
  waypoints = [],
  onRouteNameChange,
  onRouteDescriptionChange,
}) => {
  const route = routeDraft.route;
  if (!route) {
    return (
      <div className="route-summary route-summary-empty">
        <BsInfoCircle size={22} className="empty-icon" />
        <p>No route generated yet.</p>
        <span>Add at least 2 stops and click "Generate Route Path".</span>
      </div>
    );
  }

  const { distanceMeters = 0, durationSeconds = 0 } = route.summary || {};

  return (
    <div className="route-summary">
      {/* Header */}
      <header className="route-summary-header">
        <div className="header-icon-badge">
          <BsSignpostSplit size={18} />
        </div>
        <div>
          <h4>Route Metrics</h4>
          <span className="summary-subtitle">
            Calculated distance & travel estimates
          </span>
        </div>
      </header>

      {/* Overview Metric Cards */}
      <div className="route-summary-overview">
        <div className="route-summary-stat">
          <div className="stat-icon-wrapper blue">
            <BsSpeedometer2 size={18} />
          </div>
          <div className="stat-info">
            <span className="route-summary-stat-label">Total Distance</span>
            <strong>{formatDistance(distanceMeters)}</strong>
          </div>
        </div>

        <div className="route-summary-stat">
          <div className="stat-icon-wrapper orange">
            <BsClockHistory size={18} />
          </div>
          <div className="stat-info">
            <span className="route-summary-stat-label">Est. Duration</span>
            <strong>{formatDuration(durationSeconds)}</strong>
          </div>
        </div>
      </div>

      {/* Route Legs */}
      <div className="route-summary-section">
        <div className="legs-header">
          <h5>Route Segments</h5>
          <span className="legs-count-chip">
            {route.legs?.length + 1 || 0} Stops
          </span>
        </div>

        <div className="route-summary-legs">
          {route.legs?.map((leg, index) => {
            const fromWayPoint = waypoints.find(
              (waypoint) => waypoint.waypointId === leg.fromWaypointId,
            );
            const toWayPoint = waypoints.find(
              (waypoint) => waypoint.waypointId === leg.toWaypointId,
            );

            return (
              <div key={leg.sequence ?? index} className="route-summary-leg">
                <div className="leg-indicator">
                  <div className="leg-badge">{index + 1}</div>
                </div>

                <div className="route-summary-leg-content">
                  <div className="route-summary-leg-route">
                    <span className="point-name">
                      <BsGeoAltFill size={10} className="point-icon start" />
                      {fromWayPoint?.name || "Start Stop"}
                    </span>
                    <BsArrowRight size={14} className="leg-arrow" />
                    <span className="point-name">
                      <BsGeoAltFill size={10} className="point-icon end" />
                      {toWayPoint?.name || "Next Stop"}
                    </span>
                  </div>

                  <div className="route-summary-leg-meta">
                    <span className="meta-pill">
                      {formatDistance(leg.distanceMeters)}
                    </span>
                    <span className="meta-divider">•</span>
                    <span className="meta-pill">
                      {formatDuration(leg.durationSeconds)}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default RouteSummary;
